import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  PlayIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import { Textarea } from '../../components/common/Textarea';
import { Checkbox } from '../../components/common/Checkbox';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  PimAttribute, 
  PimAttributeFilters, 
  PimAttributeFormData,
  PimAttributeGroup,
  ProductType,
  PimAttributeType,
  PimAttributeValidation,
  PimAttributeOptions,
  AttributePreview,
  ValidationTest,
  AttributeBulkOperation,
  AttributeUsageStatistics,
} from '../../types/pim';

export const AttributesManagement = () => {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  // State management
  const [filters, setFilters] = useState<PimAttributeFilters>({
    page: 1,
    limit: 20,
    sort_by: 'sort_order',
    sort_direction: 'asc'
  });
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [showValidationTestModal, setShowValidationTestModal] = useState(false);

  const [selectedAttribute, setSelectedAttribute] = useState<PimAttribute | null>(null);

  // Fetch attribute usage statistics
  const { data: usageData, isLoading: loadingUsage } = useQuery({
    queryKey: ['pim-attribute-usage', selectedAttribute?.id],
    queryFn: () => pimService.getAttributeUsage(selectedAttribute!.id),
    enabled: showUsageModal && !!selectedAttribute
  });
  const [selectedAttributes, setSelectedAttributes] = useState<number[]>([]);
  const [previewData, setPreviewData] = useState<AttributePreview | null>(null);
  const [validationTests, setValidationTests] = useState<ValidationTest[]>([]);
  const [bulkOperation, setBulkOperation] = useState<AttributeBulkOperation>({
    action: 'activate',
    attributeIds: []
  });

  // Ref for file input
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<PimAttributeFormData>({
    group_id: 0,
    name: '',
    code: '',
    type: 'string' as PimAttributeType,
    description: '',
    position: 1,
    is_required: false,
    is_unique: false,
    is_filterable: false,
    is_searchable: false,
    is_active: true,
    validation_json: {},
    options_json: {}
  });

  // Fetch data
  const { data: attributesData, isLoading: loadingAttributes, error: attributesError } = useQuery({
    queryKey: ['pim-attributes', filters],
    queryFn: () => pimService.getAttributes(filters),
    keepPreviousData: true
  });

  const { data: attributeGroupsData } = useQuery({
    queryKey: ['pim-attribute-groups-dropdown'],
    queryFn: () => pimService.getAttributeGroupsForDropdown()
  });

  const { data: productTypesData } = useQuery({
    queryKey: ['pim-product-types-dropdown'],
    queryFn: () => pimService.getProductTypesForDropdown()
  });

  // Set default group_id when attribute groups data loads and form has no valid group_id
  useEffect(() => {
    if (attributeGroupsData?.data?.[0]?.id && formData.group_id === 0) {
      setFormData(prev => ({
        ...prev,
        group_id: attributeGroupsData.data[0].id
      }));
    }
  }, [attributeGroupsData]);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: PimAttributeFormData) => pimService.createAttribute(data),
    onSuccess: () => {
      showNotification(t('pim.attributes.messages.created'), 'success');
      queryClient.invalidateQueries(['pim-attributes']);
      setShowCreateModal(false);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || t('pim.attributes.messages.error'), 'error');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<PimAttributeFormData> }) =>
      pimService.updateAttribute(id, data),
    onSuccess: () => {
      showNotification(t('pim.attributes.messages.updated'), 'success');
      queryClient.invalidateQueries(['pim-attributes']);
      setShowEditModal(false);
      resetForm();
    },
    onError: (error: any) => {
      showNotification(error.message || t('pim.attributes.messages.error'), 'error');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => pimService.deleteAttribute(id),
    onSuccess: () => {
      showNotification(t('pim.attributes.messages.deleted'), 'success');
      queryClient.invalidateQueries(['pim-attributes']);
      setShowDeleteModal(false);
      setSelectedAttribute(null);
    },
    onError: (error: any) => {
      showNotification(error.message || t('pim.attributes.messages.error'), 'error');
    }
  });

  // Helper functions
  const resetForm = () => {
    setFormData({
      group_id: attributeGroupsData?.data?.[0]?.id || 0,
      name: '',
      code: '',
      type: 'string',
      description: '',
      position: 1,
      is_required: false,
      is_unique: false,
      is_filterable: false,
      is_searchable: false,
      is_active: true,
      validation_json: {},
      options_json: {}
    });
    setSelectedAttribute(null);
    setPreviewData(null);
    setValidationTests([]);
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({
      ...prev,
      search: value || undefined,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof PimAttributeFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      // Only reset to page 1 if we're changing a filter, not the page itself
      ...(key !== 'page' ? { page: 1 } : {})
    }));
  };

  const handleSort = (sortBy: string) => {
    setFilters(prev => ({
      ...prev,
      sort_by: sortBy,
      sort_direction: prev.sort_by === sortBy && prev.sort_direction === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  // Auto-generate code from name
  const handleNameChange = (name: string) => {
    setFormData(prev => ({
      ...prev,
      name,
      code: name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    }));
    updatePreview();
  };

  // Handle type change and update validation/options accordingly
  const handleTypeChange = (type: PimAttributeType) => {
    setFormData(prev => ({
      ...prev,
      type,
      validation_json: getDefaultValidationForType(type),
      options_json: getDefaultOptionsForType(type)
    }));
    updatePreview();
  };

  const getDefaultValidationForType = (type: PimAttributeType): PimAttributeValidation => {
    switch (type) {
      case 'string':
        return { maxLength: 255, minLength: 1 };
      case 'number':
        return { min: 0 };
      case 'text':
        return { maxLength: 5000 };
      default:
        return {};
    }
  };

  const getDefaultOptionsForType = (type: PimAttributeType): PimAttributeOptions => {
    switch (type) {
      case 'enum':
      case 'multiselect':
        return { options: ['Option 1', 'Option 2', 'Option 3'] };
      case 'media':
        return { 
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif'],
          maxFileSize: 5242880, // 5MB
          maxFiles: 1
        };
      case 'relation':
        return { entityType: 'Product', entityField: 'id' };
      default:
        return {};
    }
  };

  // Preview functionality
  const updatePreview = () => {
    if (formData.name) {
      // Create a clean copy to avoid circular reference issues
      const cleanFormData: PimAttributeFormData = {
        group_id: formData.group_id,
        name: formData.name,
        code: formData.code,
        type: formData.type,
        description: formData.description,
        position: formData.position,
        is_required: formData.is_required,
        is_unique: formData.is_unique,
        is_filterable: formData.is_filterable,
        is_searchable: formData.is_searchable,
        is_active: formData.is_active,
        validation_json: formData.validation_json ? JSON.parse(JSON.stringify(formData.validation_json)) : {},
        options_json: formData.options_json ? JSON.parse(JSON.stringify(formData.options_json)) : {}
      };

      setPreviewData({
        attribute: cleanFormData,
        sampleValue: getSampleValueForType(formData.type)
      });
    }
  };

  const getSampleValueForType = (type: PimAttributeType): any => {
    switch (type) {
      case 'string':
        return 'Sample text';
      case 'text':
        return 'This is a longer sample text that demonstrates how a text attribute would appear in the product form.';
      case 'number':
        return 42;
      case 'boolean':
        return true;
      case 'date':
        return new Date().toISOString().split('T')[0];
      case 'enum':
        return formData.options_json?.options?.[0] || 'Option 1';
      case 'multiselect':
        return formData.options_json?.options?.slice(0, 2) || ['Option 1', 'Option 2'];
      case 'json':
        return { key: 'value', nested: { property: 123 } };
      case 'relation':
        return { id: 1, name: 'Related Product' };
      case 'media':
        return { url: 'https://example.com/image.jpg', type: 'image/jpeg', size: 1024000 };
      default:
        return null;
    }
  };

  // Validation testing
  const runValidationTest = (input: string): ValidationTest => {
    try {
      // Simulate validation based on attribute type and rules
      const validation = formData.validation_json || {};
      
      switch (formData.type) {
        case 'string':
          if (validation.minLength && input.length < validation.minLength) {
            return {
              input,
              isValid: false,
              errorMessage: `Minimum length is ${validation.minLength}`
            };
          }
          if (validation.maxLength && input.length > validation.maxLength) {
            return {
              input,
              isValid: false,
              errorMessage: `Maximum length is ${validation.maxLength}`
            };
          }
          if (validation.regex && !new RegExp(validation.regex).test(input)) {
            return {
              input,
              isValid: false,
              errorMessage: 'Does not match required pattern'
            };
          }
          break;
        case 'number':
          const num = parseFloat(input);
          if (isNaN(num)) {
            return {
              input,
              isValid: false,
              errorMessage: 'Must be a valid number'
            };
          }
          if (validation.min !== undefined && num < validation.min) {
            return {
              input,
              isValid: false,
              errorMessage: `Minimum value is ${validation.min}`
            };
          }
          if (validation.max !== undefined && num > validation.max) {
            return {
              input,
              isValid: false,
              errorMessage: `Maximum value is ${validation.max}`
            };
          }
          break;
        case 'enum':
          if (formData.options_json?.options && !formData.options_json.options.includes(input)) {
            return {
              input,
              isValid: false,
              errorMessage: `Must be one of: ${formData.options_json.options.join(', ')}`
            };
          }
          break;
        case 'json':
          try {
            JSON.parse(input);
          } catch {
            return {
              input,
              isValid: false,
              errorMessage: 'Must be valid JSON'
            };
          }
          break;
      }
      
      return { input, isValid: true };
    } catch (error) {
      return {
        input,
        isValid: false,
        errorMessage: 'Validation error'
      };
    }
  };

  // Render preview field based on attribute type
  const renderPreviewField = (preview: AttributePreview) => {
    const { attribute, sampleValue } = preview;

    switch (attribute.type) {
      case 'string':
        return (
          <Input
            label={attribute.name}
            value={sampleValue}
            placeholder={attribute.description || ''}
            disabled
          />
        );
      case 'text':
        return (
          <Textarea
            label={attribute.name}
            value={sampleValue}
            placeholder={attribute.description || ''}
            disabled
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            label={attribute.name}
            value={sampleValue}
            disabled
          />
        );
      case 'boolean':
        return (
          <Checkbox
            label={attribute.name}
            checked={sampleValue}
            disabled
          />
        );
      case 'date':
        return (
          <Input
            type="date"
            label={attribute.name}
            value={sampleValue}
            disabled
          />
        );
      case 'enum':
        return (
          <Select
            label={attribute.name}
            value={sampleValue}
            disabled
          >
            {attribute.options_json?.options?.map((option: string) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </Select>
        );
      case 'multiselect':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attribute.name}
            </label>
            <div className="space-y-2">
              {attribute.options_json?.options?.map((option: string) => (
                <Checkbox
                  key={option}
                  label={option}
                  checked={sampleValue?.includes(option)}
                  disabled
                />
              ))}
            </div>
          </div>
        );
      case 'json':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {attribute.name}
            </label>
            <pre className="bg-gray-100 p-3 rounded text-sm">
              {JSON.stringify(sampleValue, null, 2)}
            </pre>
          </div>
        );
      default:
        return (
          <Input
            label={attribute.name}
            value={String(sampleValue || '')}
            disabled
          />
        );
    }
  };

  // Bulk operations
  const handleBulkOperation = async () => {
    if (selectedAttributes.length === 0) {
      showNotification(t('pim.common.noItemsSelected'), 'warning');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      // Execute bulk operation based on action type
      switch (bulkOperation.action) {
        case 'activate':
          for (const attrId of selectedAttributes) {
            try {
              await pimService.updateAttribute(attrId, { is_active: true });
              successCount++;
            } catch (error) {
              errorCount++;
            }
          }
          showNotification(
            t('pim.attributes.messages.bulkActivated', { count: successCount }),
            successCount > 0 ? 'success' : 'error'
          );
          break;

        case 'deactivate':
          for (const attrId of selectedAttributes) {
            try {
              await pimService.updateAttribute(attrId, { is_active: false });
              successCount++;
            } catch (error) {
              errorCount++;
            }
          }
          showNotification(
            t('pim.attributes.messages.bulkDeactivated', { count: successCount }),
            successCount > 0 ? 'success' : 'error'
          );
          break;

        case 'delete':
          for (const attrId of selectedAttributes) {
            try {
              await pimService.deleteAttribute(attrId);
              successCount++;
            } catch (error) {
              errorCount++;
            }
          }
          showNotification(
            t('pim.attributes.messages.bulkDeleted', { count: successCount }),
            successCount > 0 ? 'success' : 'error'
          );
          break;

        case 'change_group':
          if (!bulkOperation.data?.groupId) {
            showNotification(t('pim.attributes.messages.selectGroup'), 'error');
            return;
          }
          for (const attrId of selectedAttributes) {
            try {
              await pimService.updateAttribute(attrId, {
                group_id: bulkOperation.data.groupId
              });
              successCount++;
            } catch (error) {
              errorCount++;
            }
          }
          showNotification(
            t('pim.attributes.messages.bulkGroupChanged', { count: successCount }),
            successCount > 0 ? 'success' : 'error'
          );
          break;

        default:
          showNotification(t('pim.common.invalidAction'), 'error');
          return;
      }

      // Show error summary if any failed
      if (errorCount > 0) {
        showNotification(
          `${errorCount} ${t('pim.attributes.messages.operationsFailed')}`,
          'warning'
        );
      }

      // Refresh data and reset state
      queryClient.invalidateQueries(['pim-attributes']);
      setShowBulkModal(false);
      setBulkOperation({ action: 'activate', attributeIds: [] });
      setSelectedAttributes([]);

    } catch (error: any) {
      showNotification(error.message || t('pim.attributes.messages.bulkOperationFailed'), 'error');
    }
  };

  // Import/Export functionality
  const handleExport = () => {
    const dataToExport = attributesData?.data || [];

    // Transform data to import-friendly format
    const exportData = dataToExport.map(attr => ({
      code: attr.code,
      name: attr.label, // Use 'name' for import compatibility
      label: attr.label, // Keep label too for flexibility
      type: attr.type,
      group_id: attr.group_id,
      description: attr.description,
      position: attr.position || attr.sort_order,
      is_required: attr.is_required || attr.required,
      is_unique: attr.is_unique || attr.unique,
      is_filterable: attr.is_filterable,
      is_searchable: attr.is_searchable,
      is_active: attr.is_active,
      validation_json: attr.validation_json,
      options_json: attr.options_json
    }));

    const jsonData = JSON.stringify(exportData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attributes-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string);

        // Validate imported data structure
        if (!Array.isArray(importedData)) {
          showNotification(t('pim.importExport.messages.invalidFormat'), 'error');
          return;
        }

        let successCount = 0;
        let errorCount = 0;
        let skippedCount = 0;

        // Import each attribute
        for (const attrData of importedData) {
          try {
            // Skip if missing required fields (check both name and label)
            const hasName = attrData.name || attrData.label;
            if (!hasName || !attrData.code || !attrData.type) {
              skippedCount++;
              continue;
            }

            // Map data to expected format
            const formattedData: Partial<PimAttributeFormData> = {
              group_id: attrData.group_id || attrData.groupId,
              name: attrData.name || attrData.label,
              code: attrData.code,
              type: attrData.type,
              description: attrData.description,
              position: attrData.position || attrData.sort_order || 1,
              is_required: attrData.is_required || attrData.isRequired || false,
              is_unique: attrData.is_unique || attrData.isUnique || false,
              is_filterable: attrData.is_filterable || attrData.isFilterable || false,
              is_searchable: attrData.is_searchable || attrData.isSearchable || false,
              validation_json: attrData.validation_json || attrData.validationRules || {},
              options_json: attrData.options_json || attrData.options || {}
            };

            // Try to create new attribute
            await pimService.createAttribute(formattedData as PimAttributeFormData);
            successCount++;
          } catch (error: any) {
            // If code already exists, try to update
            if (error.message?.includes('already exists') || error.message?.includes('unique')) {
              try {
                // Find existing attribute by code
                const existing = attributesData?.data?.find(a => a.code === attrData.code);
                if (existing) {
                  await pimService.updateAttribute(existing.id, attrData);
                  successCount++;
                } else {
                  errorCount++;
                }
              } catch (updateError) {
                errorCount++;
              }
            } else {
              errorCount++;
            }
          }
        }

        // Show import summary
        if (successCount > 0) {
          showNotification(
            t('pim.importExport.messages.importSuccess', { count: successCount }),
            'success'
          );
        }
        if (errorCount > 0) {
          showNotification(
            `${errorCount} ${t('pim.attributes.messages.importFailed')}`,
            'warning'
          );
        }
        if (skippedCount > 0) {
          showNotification(
            `${skippedCount} ${t('pim.attributes.messages.importSkipped')}`,
            'info'
          );
        }

        // Refresh data
        queryClient.invalidateQueries(['pim-attributes']);
        setShowImportModal(false);

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

      } catch (error) {
        showNotification(t('pim.importExport.messages.invalidFile'), 'error');
        // Reset file input on error too
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    };

    reader.onerror = () => {
      showNotification(t('pim.importExport.messages.fileReadError'), 'error');
    };

    reader.readAsText(file);

    // Reset file input
    event.target.value = '';
  };

  // Event handlers
  const handleCreate = () => {
    setShowCreateModal(true);
    resetForm();
  };

  const handleEdit = (attribute: PimAttribute) => {
    setSelectedAttribute(attribute);
    const newFormData = {
      group_id: attribute.group_id || 0,
      name: attribute.label || '',
      code: attribute.code || '',
      type: attribute.type,
      description: attribute.description || '',
      position: attribute.position || 1,
      is_required: Boolean(attribute.is_required || attribute.required),
      is_unique: Boolean(attribute.is_unique || attribute.unique),
      is_filterable: Boolean(attribute.is_filterable),
      is_searchable: Boolean(attribute.is_searchable),
      is_active: Boolean(attribute.is_active),
      validation_json: attribute.validation_json || {},
      options_json: attribute.options_json || {}
    };

    setFormData(newFormData);
    setShowEditModal(true);
  };

  const handleDelete = (attribute: PimAttribute) => {
    setSelectedAttribute(attribute);
    setShowDeleteModal(true);
  };

  const handlePreview = () => {
    updatePreview();
    setShowPreviewModal(true);
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.name || !formData.code || !formData.group_id || formData.group_id === 0) {
      showNotification(t('common.validation.required'), 'error');
      return;
    }

    if (showEditModal && selectedAttribute) {
      updateMutation.mutate({ id: selectedAttribute.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sort_by: 'sort_order',
      sort_direction: 'asc'
    });
  };

  const renderSortableHeader = (label: string, sortKey: string) => {
    const isActive = filters.sort_by === sortKey;
    const isAsc = filters.sort_direction === 'asc';

    return (
      <th
        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(sortKey)}
      >
        <div className="flex items-center gap-1">
          <span>{label}</span>
          {isActive && (
            isAsc ?
              <ChevronUpIcon className="h-4 w-4" /> :
              <ChevronDownIcon className="h-4 w-4" />
          )}
        </div>
      </th>
    );
  };

  // Helper function to render type-specific form fields
  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 'string':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('pim.attributes.types.string')} {t('pim.attributes.form.validation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  label={t('pim.attributes.form.minLength')}
                  type="number"
                  value={formData.validation_json?.minLength ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation_json: {
                      ...prev.validation_json,
                      minLength: parseInt(e.target.value) || undefined
                    }
                  }))}
                  placeholder={t('pim.attributes.form.minLength')}
                />
                <Input
                  label={t('pim.attributes.form.maxLength')}
                  type="number"
                  value={formData.validation_json?.maxLength ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation_json: {
                      ...prev.validation_json,
                      maxLength: parseInt(e.target.value) || undefined
                    }
                  }))}
                  placeholder={t('pim.attributes.form.maxLength')}
                />
                <Input
                  label={t('pim.attributes.form.pattern')}
                  value={formData.validation_json?.regex || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation_json: {
                      ...prev.validation_json,
                      regex: e.target.value || undefined
                    }
                  }))}
                  placeholder={t('pim.attributes.form.pattern')}
                />
              </div>
            </CardContent>
          </Card>
        );

      case 'number':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('pim.attributes.types.number')} {t('pim.attributes.form.validation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Input
                  label={t('pim.attributes.form.minValue')}
                  type="number"
                  value={formData.validation_json?.min ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation_json: {
                      ...prev.validation_json,
                      min: parseFloat(e.target.value) || undefined
                    }
                  }))}
                  placeholder={t('pim.attributes.form.minValue')}
                />
                <Input
                  label={t('pim.attributes.form.maxValue')}
                  type="number"
                  value={formData.validation_json?.max ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation_json: {
                      ...prev.validation_json,
                      max: parseFloat(e.target.value) || undefined
                    }
                  }))}
                  placeholder={t('pim.attributes.form.maxValue')}
                />
                <Input
                  label={t('pim.attributes.form.minValue')}
                  type="number"
                  min="0"
                  max="10"
                  value={formData.validation_json?.decimalPlaces ?? ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation_json: {
                      ...prev.validation_json,
                      decimalPlaces: parseInt(e.target.value) || undefined
                    }
                  }))}
                  placeholder={t('pim.attributes.form.minValue')}
                />
                <Input
                  label={t('common.name')}
                  value={formData.validation_json?.unit || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    validation_json: {
                      ...prev.validation_json,
                      unit: e.target.value || undefined
                    }
                  }))}
                  placeholder="kg, cm, %"
                />
              </div>
            </CardContent>
          </Card>
        );

      case 'enum':
      case 'multiselect':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{formData.type === 'enum' ? t('pim.attributes.types.enum') : t('pim.attributes.types.multiselect')} {t('pim.attributes.options')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('pim.attributes.options')}</label>
                <div className="space-y-2">
                  {(formData.options_json?.options || []).map((option, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...(formData.options_json?.options || [])];
                          newOptions[index] = e.target.value;
                          setFormData(prev => ({
                            ...prev,
                            options_json: {
                              ...prev.options_json,
                              options: newOptions
                            }
                          }));
                        }}
                        placeholder={`${t('pim.attributes.form.addOption')} ${index + 1}`}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        icon={XMarkIcon}
                        onClick={() => {
                          const newOptions = [...(formData.options_json?.options || [])];
                          newOptions.splice(index, 1);
                          setFormData(prev => ({
                            ...prev,
                            options_json: {
                              ...prev.options_json,
                              options: newOptions
                            }
                          }));
                        }}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    icon={PlusIcon}
                    onClick={() => {
                      const newOptions = [...(formData.options_json?.options || []), ''];
                      setFormData(prev => ({
                        ...prev,
                        options_json: {
                          ...prev.options_json,
                          options: newOptions
                        }
                      }));
                    }}
                  >
                    {t('pim.attributes.form.addOption')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'media':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('pim.attributes.types.media')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('pim.settings.media.allowedFileTypes')}</label>
                  <div className="space-y-2">
                    {['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'].map(type => (
                      <Checkbox
                        key={type}
                        checked={(formData.options_json?.allowedTypes || []).includes(type)}
                        onChange={(e) => {
                          const allowedTypes = formData.options_json?.allowedTypes || [];
                          const newTypes = e.target.checked
                            ? [...allowedTypes, type]
                            : allowedTypes.filter(t => t !== type);
                          setFormData(prev => ({
                            ...prev,
                            options_json: {
                              ...prev.options_json,
                              allowedTypes: newTypes
                            }
                          }));
                        }}
                        label={type}
                      />
                    ))}
                  </div>
                </div>
                <div className="space-y-4">
                  <Input
                    label={t('pim.settings.media.maxFileSize')}
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={formData.options_json?.maxFileSize ? (formData.options_json.maxFileSize / 1024 / 1024).toString() : ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      options_json: {
                        ...prev.options_json,
                        maxFileSize: parseFloat(e.target.value) * 1024 * 1024 || undefined
                      }
                    }))}
                    placeholder={t('pim.settings.media.maxFileSize')}
                  />
                  <Input
                    label={t('pim.common.enterValue', { name: 'Max Files' })}
                    type="number"
                    min="1"
                    value={formData.options_json?.maxFiles ?? ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      options_json: {
                        ...prev.options_json,
                        maxFiles: parseInt(e.target.value) || undefined
                      }
                    }))}
                    placeholder={t('pim.common.enterValue', { name: 'files' })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );

      case 'relation':
        return (
          <Card>
            <CardHeader>
              <CardTitle>{t('pim.attributes.types.relation')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label={t('pim.attributes.types.relation')}
                  value={formData.options_json?.entityType || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    options_json: {
                      ...prev.options_json,
                      entityType: e.target.value || undefined
                    }
                  }))}
                  options={[
                    { value: '', label: t('pim.common.selectOption') },
                    { value: 'Product', label: t('pim.products.title') },
                    { value: 'ProductType', label: t('pim.productTypes.title') },
                    { value: 'Category', label: t('pim.products.category') },
                    { value: 'Brand', label: t('pim.products.name') }
                  ]}
                />
                <Input
                  label={t('common.name')}
                  value={formData.options_json?.entityField || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    options_json: {
                      ...prev.options_json,
                      entityField: e.target.value || undefined
                    }
                  }))}
                  placeholder={t('pim.common.enterValue', { name: 'field' })}
                />
              </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  // Check permissions
  if (!permissions.canManageAttributes) {
    return (
      <div className="text-center py-12">
        <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.common.accessDenied')}</h3>
        <p className="mt-1 text-sm text-gray-500">
          {t('pim.common.accessDeniedDescription')}
        </p>
      </div>
    );
  }

  if (attributesError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{t('pim.common.loadingError')}: {(attributesError as any).message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('pim.attributes.title')}</h1>
          <p className="text-gray-600">{t('pim.attributes.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            icon={ArrowDownTrayIcon}
            onClick={handleExport}
          >
            {t('pim.importExport.export')}
          </Button>
          <label className="cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
            />
            <span className="inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-primary-500 px-4 py-2 text-sm rounded-md">
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              {t('pim.importExport.import')}
            </span>
          </label>
          <Button icon={PlusIcon} onClick={handleCreate}>
            {t('pim.attributes.addAttribute')}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder={t('pim.attributes.searchPlaceholder')}
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                icon={MagnifyingGlassIcon}
              />
            </div>
            <div className="flex gap-2">
              <Select
                value={filters.group_id?.join(',') || ''}
                onChange={(e) => handleFilterChange('group_id', e.target.value ? [parseInt(e.target.value)] : undefined)}
                options={[
                  { value: '', label: t('pim.attributes.allGroups') },
                  ...(attributeGroupsData?.data?.map(group => ({
                    value: group.id.toString(),
                    label: group.name
                  })) || [])
                ]}
              />
              <Select
                value={typeof filters.type === 'string' ? filters.type : (Array.isArray(filters.type) ? filters.type[0] : '')}
                onChange={(e) => handleFilterChange('type', e.target.value || undefined)}
                options={[
                  { value: '', label: t('pim.attributes.allTypes') },
                  { value: 'string', label: t('pim.attributes.types.string') },
                  { value: 'text', label: t('pim.attributes.types.text') },
                  { value: 'number', label: t('pim.attributes.types.number') },
                  { value: 'boolean', label: t('pim.attributes.types.boolean') },
                  { value: 'date', label: t('pim.attributes.types.date') },
                  { value: 'enum', label: t('pim.attributes.types.enum') },
                  { value: 'multiselect', label: t('pim.attributes.types.multiselect') },
                  { value: 'json', label: t('pim.attributes.types.json') },
                  { value: 'relation', label: t('pim.attributes.types.relation') },
                  { value: 'media', label: t('pim.attributes.types.media') }
                ]}
              />
              <Select
                value={filters.limit?.toString() || '20'}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                options={[
                  { value: '20', label: '20' },
                  { value: '40', label: '40' },
                  { value: '60', label: '60' },
                  { value: '100', label: '100' },
                  { value: '200', label: '200' }
                ]}
              />
              {(filters.search || filters.group_id || filters.type) && (
                <Button
                  variant="outline"
                  icon={XMarkIcon}
                  onClick={clearFilters}
                >
                  {t('common.clear')}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedAttributes.length > 0 && (
        <Card>
          <CardContent>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">
                {t('pim.products.selected', { count: selectedAttributes.length })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setBulkOperation({ action: 'delete', attributeIds: selectedAttributes });
                    setShowBulkModal(true);
                  }}
                >
                  {t('common.actions.delete')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setBulkOperation({ action: 'activate', attributeIds: selectedAttributes });
                    setShowBulkModal(true);
                  }}
                >
                  {t('common.actions.activate')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedAttributes([])}
                >
                  {t('common.clear')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Attributes Table */}
      <Card>
        <CardContent>
          {loadingAttributes ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-gray-600">{t('pim.attributes.loading')}</p>
            </div>
          ) : !attributesData?.data?.length ? (
            <div className="text-center py-12">
              <CogIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.attributes.noAttributes')}</h3>
              <p className="mt-1 text-sm text-gray-500">{t('pim.attributes.noAttributesDescription')}</p>
              <div className="mt-6">
                <Button icon={PlusIcon} onClick={handleCreate}>
                  {t('pim.attributes.addAttribute')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedAttributes.length === attributesData.data.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedAttributes(attributesData.data.map(attr => attr.id));
                          } else {
                            setSelectedAttributes([]);
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                    </th>
                    {renderSortableHeader('Kolejność sortowania', 'sort_order')}
                    {renderSortableHeader(t('pim.attributes.name'), 'label')}
                    {renderSortableHeader(t('pim.attributes.code'), 'code')}
                    {renderSortableHeader(t('pim.attributes.type'), 'type')}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributes.group')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributes.form.validation')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributes.status')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('pim.attributes.usage')}
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('common.actions.title')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attributesData.data.map((attribute) => (
                    <tr key={attribute.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedAttributes.includes(attribute.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAttributes([...selectedAttributes, attribute.id]);
                            } else {
                              setSelectedAttributes(selectedAttributes.filter(id => id !== attribute.id));
                            }
                          }}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-600">
                          {attribute.position}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{attribute.label}</div>
                        {attribute.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {attribute.description}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <code className="text-sm bg-gray-100 px-2 py-1 rounded">{attribute.code}</code>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant="secondary">{attribute.type}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{attribute.group?.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          {/* Required/Unique/Filterable/Searchable flags */}
                          <div className="flex flex-wrap gap-1">
                            {(attribute.is_required || attribute.required) && (
                              <Badge variant="outline" size="sm">{t('pim.attributes.required')}</Badge>
                            )}
                            {(attribute.is_unique || attribute.unique) && (
                              <Badge variant="outline" size="sm">{t('pim.attributes.unique')}</Badge>
                            )}
                            {attribute.is_filterable && (
                              <Badge variant="outline" size="sm">{t('pim.attributes.filterable')}</Badge>
                            )}
                            {attribute.is_searchable && (
                              <Badge variant="outline" size="sm">{t('pim.attributes.searchable')}</Badge>
                            )}
                          </div>

                          {/* Validation rules from validation_json */}
                          {attribute.validation_json && Object.keys(attribute.validation_json).length > 0 && (
                            <div className="text-xs text-gray-600 space-y-0.5">
                              {attribute.type === 'string' && (
                                <>
                                  {attribute.validation_json.minLength && (
                                    <div>Min: {attribute.validation_json.minLength}</div>
                                  )}
                                  {attribute.validation_json.maxLength && (
                                    <div>Max: {attribute.validation_json.maxLength}</div>
                                  )}
                                  {attribute.validation_json.regex && (
                                    <div className="truncate max-w-xs" title={attribute.validation_json.regex}>
                                      Pattern: {attribute.validation_json.regex}
                                    </div>
                                  )}
                                </>
                              )}
                              {attribute.type === 'number' && (
                                <>
                                  {attribute.validation_json.min !== undefined && (
                                    <div>Min: {attribute.validation_json.min}</div>
                                  )}
                                  {attribute.validation_json.max !== undefined && (
                                    <div>Max: {attribute.validation_json.max}</div>
                                  )}
                                  {attribute.validation_json.decimalPlaces !== undefined && (
                                    <div>Decimals: {attribute.validation_json.decimalPlaces}</div>
                                  )}
                                  {attribute.validation_json.unit && (
                                    <div>Unit: {attribute.validation_json.unit}</div>
                                  )}
                                </>
                              )}
                              {(attribute.type === 'date' || attribute.type === 'datetime') && (
                                <>
                                  {attribute.validation_json.minDate && (
                                    <div>From: {attribute.validation_json.minDate}</div>
                                  )}
                                  {attribute.validation_json.maxDate && (
                                    <div>To: {attribute.validation_json.maxDate}</div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {attribute.is_active ? (
                          <Badge variant="success" icon={CheckCircleIcon}>{t('pim.common.active')}</Badge>
                        ) : (
                          <Badge variant="secondary" icon={XCircleIcon}>{t('pim.common.inactive')}</Badge>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={ChartBarIcon}
                          onClick={() => {
                            setSelectedAttribute(attribute);
                            setShowUsageModal(true);
                          }}
                        >
                          {t('pim.apiKeys.viewUsage')}
                        </Button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={PencilIcon}
                            onClick={() => handleEdit(attribute)}
                          >
                            {t('common.edit')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={TrashIcon}
                            onClick={() => handleDelete(attribute)}
                          >
                            {t('common.delete')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {attributesData?.pagination && (
            <div className="mt-6">
              <Pagination
                currentPage={attributesData.pagination.page}
                totalPages={attributesData.pagination.totalPages}
                totalItems={attributesData.pagination.total}
                itemsPerPage={attributesData.pagination.limit}
                onPageChange={(page) => handleFilterChange('page', page)}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showCreateModal || showEditModal}
        onClose={() => {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }}
        title={showEditModal ? t('pim.attributes.editAttribute') : t('pim.attributes.addAttribute')}
        size="xl"
      >
        <div className="space-y-6 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t('pim.attributes.name')}
              value={formData.name || ''}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder={t('pim.common.enterValue', { name: t('pim.attributes.name').toLowerCase() })}
            />
            <Input
              label={t('pim.attributes.code')}
              value={formData.code || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
              required
              placeholder={t('pim.common.enterValue', { name: t('pim.attributes.code').toLowerCase() })}
              help={t('pim.attributes.form.attributeCode')}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label={t('pim.attributes.group')}
              value={formData.group_id ? formData.group_id.toString() : '0'}
              onChange={(e) => setFormData(prev => ({ ...prev, group_id: parseInt(e.target.value) }))}
              required
              error={formData.group_id === 0 ? t('pim.common.selectOption') : undefined}
              options={[
                { value: '0', label: t('pim.common.selectOption'), disabled: true },
                ...(attributeGroupsData?.data?.map(group => ({
                  value: group.id.toString(),
                  label: group.name
                })) || [])
              ]}
            />
            <Select
              label={t('pim.attributes.type')}
              value={formData.type}
              onChange={(e) => handleTypeChange(e.target.value as PimAttributeType)}
              required
              options={[
                { value: 'string', label: t('pim.attributes.types.string') },
                { value: 'text', label: t('pim.attributes.types.text') },
                { value: 'number', label: t('pim.attributes.types.number') },
                { value: 'boolean', label: t('pim.attributes.types.boolean') },
                { value: 'date', label: t('pim.attributes.types.date') },
                { value: 'enum', label: t('pim.attributes.types.enum') },
                { value: 'multiselect', label: t('pim.attributes.types.multiselect') },
                { value: 'json', label: t('pim.attributes.types.json') },
                { value: 'relation', label: t('pim.attributes.types.relation') },
                { value: 'media', label: t('pim.attributes.types.media') }
              ]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label={t('pim.attributes.position')}
              type="number"
              value={formData.position ?? 1}
              onChange={(e) => setFormData(prev => ({ ...prev, position: parseInt(e.target.value) || 1 }))}
              placeholder={t('pim.common.enterValue', { name: t('pim.attributes.position').toLowerCase() })}
              help={t('pim.attributeGroups.positionHelp')}
            />
            <Select
              label={t('pim.attributes.required')}
              value={formData.is_required ? 'true' : 'false'}
              onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.value === 'true' }))}
              options={[
                { value: 'false', label: t('common.no') },
                { value: 'true', label: t('common.yes') }
              ]}
            />
            <Select
              label={t('pim.attributes.unique')}
              value={formData.is_unique ? 'true' : 'false'}
              onChange={(e) => setFormData(prev => ({ ...prev, is_unique: e.target.value === 'true' }))}
              options={[
                { value: 'false', label: t('common.no') },
                { value: 'true', label: t('common.yes') }
              ]}
            />
            <Select
              label={t('pim.attributes.filterable')}
              value={formData.is_filterable ? 'true' : 'false'}
              onChange={(e) => setFormData(prev => ({ ...prev, is_filterable: e.target.value === 'true' }))}
              options={[
                { value: 'false', label: t('common.no') },
                { value: 'true', label: t('common.yes') }
              ]}
            />
            <Select
              label={t('pim.attributes.searchable')}
              value={formData.is_searchable ? 'true' : 'false'}
              onChange={(e) => setFormData(prev => ({ ...prev, is_searchable: e.target.value === 'true' }))}
              options={[
                { value: 'false', label: t('common.no') },
                { value: 'true', label: t('common.yes') }
              ]}
            />
            <Select
              label={t('pim.common.active')}
              value={formData.is_active ? 'true' : 'false'}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
              options={[
                { value: 'false', label: t('common.no') },
                { value: 'true', label: t('common.yes') }
              ]}
            />
          </div>

          <Textarea
            label={t('pim.attributes.description')}
            value={formData.description || ''}
            onChange={(e) => {
              setFormData(prev => ({ ...prev, description: e.target.value }));
            }}
            placeholder={t('pim.common.enterValue', { name: t('pim.attributes.description').toLowerCase() })}
            rows={3}
          />

          {/* Type-specific validation and options */}
          {renderTypeSpecificFields()}

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setShowEditModal(false);
                resetForm();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowValidationTestModal(true)}
              icon={PlayIcon}
            >
              {t('pim.attributes.form.validation')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {(createMutation.isLoading || updateMutation.isLoading) ? t('pim.common.saving') : t('common.save')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        title={t('common.view')}
        size="lg"
      >
        {previewData && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('common.view')}: {previewData.attribute.name}
              </h3>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">{t('pim.attributes.type')}:</span> {previewData.attribute.type}
                  </div>
                  <div>
                    <span className="font-medium">{t('pim.attributes.required')}:</span> {(previewData.attribute.is_required || previewData.attribute.required) ? t('common.yes') : t('common.no')}
                  </div>
                  <div>
                    <span className="font-medium">{t('pim.attributes.code')}:</span> <code>{previewData.attribute.code}</code>
                  </div>
                  <div>
                    <span className="font-medium">{t('pim.attributes.unique')}:</span> {(previewData.attribute.is_unique || previewData.attribute.unique) ? t('common.yes') : t('common.no')}
                  </div>
                </div>
                {previewData.attribute.description && (
                  <div>
                    <span className="font-medium">{t('pim.attributes.description')}:</span> {previewData.attribute.description}
                  </div>
                )}
              </div>
            </div>

            <div className="border p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">{t('common.view')}</h4>
              {renderPreviewField(previewData)}
            </div>

            {Object.keys(previewData.attribute.validation_json || {}).length > 0 && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">{t('pim.attributes.form.validation')}</h4>
                <pre className="text-sm text-blue-800 whitespace-pre-wrap">
                  {JSON.stringify(previewData.attribute.validation_json, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Validation Test Modal */}
      <Modal
        isOpen={showValidationTestModal}
        onClose={() => setShowValidationTestModal(false)}
        title={t('pim.attributes.form.validation')}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <Input
              label={t('pim.common.enterValue', { name: formData.type })}
              placeholder={t('pim.common.enterValue', { name: formData.type })}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const input = (e.target as HTMLInputElement).value;
                  const result = runValidationTest(input);
                  setValidationTests([result, ...validationTests.slice(0, 9)]);
                  (e.target as HTMLInputElement).value = '';
                }
              }}
            />
            <p className="text-sm text-gray-500 mt-1">{t('common.submit')}</p>
          </div>
          
          {validationTests.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-medium text-gray-900">{t('pim.attributes.form.validation')}</h4>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {validationTests.map((test, index) => (
                  <div
                    key={index}
                    className={`p-3 rounded-lg border ${
                      test.isValid
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <code className="text-sm">{test.input}</code>
                      <div className="flex items-center gap-2">
                        {test.isValid ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-600" />
                        ) : (
                          <XCircleIcon className="h-5 w-5 text-red-600" />
                        )}
                        <span className={`text-sm font-medium ${
                          test.isValid ? 'text-green-800' : 'text-red-800'
                        }`}>
                          {test.isValid ? t('common.success') : t('common.error')}
                        </span>
                      </div>
                    </div>
                    {test.errorMessage && (
                      <p className="text-sm text-red-700 mt-1">{test.errorMessage}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Usage Statistics Modal */}
      <Modal
        isOpen={showUsageModal}
        onClose={() => setShowUsageModal(false)}
        title={t('pim.apiKeys.viewUsage')}
        size="lg"
      >
        {loadingUsage ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <p className="mt-2 text-gray-600">Loading statistics...</p>
          </div>
        ) : usageData?.data ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">{usageData.data.statistics.productsUsing}</div>
                <div className="text-sm text-blue-800">Products Using</div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-600">{usageData.data.statistics.withValues}</div>
                <div className="text-sm text-green-800">With Values</div>
              </div>
              <div className="bg-yellow-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-600">{usageData.data.statistics.emptyValues}</div>
                <div className="text-sm text-yellow-800">Empty Values</div>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">{usageData.data.statistics.fillRate}%</div>
                <div className="text-sm text-purple-800">Fill Rate</div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Recent Usage</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Last used:</span>
                  <span>{usageData.data.statistics.lastUsed}</span>
                </div>
                {usageData.data.statistics.mostRecentProduct && (
                  <div className="flex justify-between">
                    <span>Most recent product:</span>
                    <span>{usageData.data.statistics.mostRecentProduct.name} (#{usageData.data.statistics.mostRecentProduct.sku})</span>
                  </div>
                )}
                {usageData.data.statistics.averageValueLength !== null && (
                  <div className="flex justify-between">
                    <span>Average value length:</span>
                    <span>{usageData.data.statistics.averageValueLength} characters</span>
                  </div>
                )}
              </div>
            </div>

            {usageData.data.statistics.productsUsing === 0 && (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> This attribute has not been used in any products yet.
                </p>
              </div>
            )}
          </div>
        ) : selectedAttribute ? (
          <div className="text-center py-12 text-gray-500">
            Failed to load statistics. Please try again.
          </div>
        ) : null}
      </Modal>

      {/* Bulk Operations Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={t('common.actions.title')}
      >
        <div className="space-y-4">
          <div>
            <Select
              label={t('common.actions.title')}
              value={bulkOperation.action}
              onChange={(e) => setBulkOperation(prev => ({ ...prev, action: e.target.value as any }))}
              options={[
                { value: 'activate', label: t('common.actions.activate') },
                { value: 'deactivate', label: t('userPage.actions.deactivate') },
                { value: 'delete', label: t('common.actions.delete') },
                { value: 'change_scope', label: t('pim.attributes.group') },
                { value: 'change_group', label: t('pim.attributes.group') }
              ]}
            />
          </div>

          {bulkOperation.action === 'change_group' && (
            <Select
              label={t('pim.attributes.group')}
              value={bulkOperation.data?.groupId || ''}
              onChange={(e) => setBulkOperation(prev => ({
                ...prev,
                data: { ...prev.data, groupId: parseInt(e.target.value) }
              }))}
              options={[
                { value: '', label: t('pim.common.selectOption') },
                ...(attributeGroupsData?.data?.map(group => ({
                  value: group.id.toString(),
                  label: group.name
                })) || [])
              ]}
            />
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">{t('common.warning')}</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    {t('pim.products.selected', { count: selectedAttributes.length })}
                    {bulkOperation.action === 'delete' && ` ${t('userManagement.deleteDialog.warning')}`}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setShowBulkModal(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant={bulkOperation.action === 'delete' ? 'danger' : 'primary'}
              onClick={handleBulkOperation}
            >
              {t('common.submit')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedAttribute(null);
        }}
        title={t('pim.attributes.deleteAttribute')}
      >
        <div className="space-y-4">
          <p>
            {t('pim.attributes.confirmDelete', { name: selectedAttribute?.name })}
          </p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">{t('common.warning')}</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    {t('pim.attributes.deleteWarning')}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedAttribute(null);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => selectedAttribute && deleteMutation.mutate(selectedAttribute.id)}
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? t('pim.common.deleting') : t('common.delete')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
